import { cloneElement, forwardRef, isValidElement, useId, type InputHTMLAttributes, type ReactElement, type ReactNode } from 'react';
import { CircleAlert } from 'lucide-react';
import { cn } from '../lib/cn.js';

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }>(
  ({ className, invalid, ...props }, ref) => (
    <input
      ref={ref}
      aria-invalid={invalid || undefined}
      className={cn(
        'h-10 w-full rounded-md border border-border-strong bg-surface px-3 text-base sm:text-sm text-fg shadow-[inset_0_1px_1px_oklch(0_0_0/0.03)]',
        'placeholder:text-fg-subtle transition-shadow focus:border-primary',
        'aria-invalid:border-danger aria-invalid:focus:shadow-[0_0_0_3px_color-mix(in_oklch,var(--dnx-danger)_25%,transparent)]',
        'disabled:bg-surface-muted disabled:text-fg-muted',
        className,
      )}
      {...props}
    />
  ),
);
Input.displayName = 'Input';

interface FieldProps {
  label: string;
  hint?: ReactNode;
  error?: string;
  labelAction?: ReactNode;
  children: ReactElement<{ id?: string; 'aria-describedby'?: string; invalid?: boolean }>;
}

/** Label visível acima, ajuda abaixo, erro associado por aria-describedby (docs/04 §5.8). */
export function Field({ label, hint, error, labelAction, children }: FieldProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy = [hint && hintId, error && errorId].filter(Boolean).join(' ') || undefined;
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-baseline justify-between gap-2">
        <label htmlFor={id} className="text-[13px] font-medium text-fg">{label}</label>
        {labelAction}
      </div>
      {isValidElement(children)
        ? cloneElement(children, { id, 'aria-describedby': describedBy, invalid: Boolean(error) })
        : children}
      {hint && !error && <p id={hintId} className="text-xs text-fg-muted">{hint}</p>}
      {error && (
        <p id={errorId} role="alert" className="flex items-center gap-1.5 text-xs font-medium text-danger">
          <CircleAlert className="size-3.5 shrink-0" aria-hidden />
          {error}
        </p>
      )}
    </div>
  );
}
